import { useState, useEffect, ReactElement } from 'react';
import { useTranslation } from 'next-i18next';
import Swiper from '~/ui/components/common/Swiper/Swiper';
import Button from '@Hotels-at-Home/hah-fe-core/dist/components/Button';
import { Link } from '@Hotels-at-Home/hah-fe-core/dist/components';
import * as siteConfig from '~/constants/siteConfig';

const HomeCollectionSection = (): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$homeCollectionSection' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tLink } = useTranslation('', {
    keyPrefix: '$local.$homePageLinks.$homeCollectionSection',
  });

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize(); // Check on initial render
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const activeCategory = 'home-collection-active-category';

  const [isActiveButton, setIsActiveButton] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedCategory = sessionStorage.getItem(activeCategory);
      return savedCategory ? parseInt(savedCategory, 10) : 1;
    }
    return 1;
  });

  const handleClick = (id: number) => {
    setIsActiveButton(id);
    sessionStorage.setItem(activeCategory, id.toString());
  };

  const { product } = siteConfig;

  const buttonSlides = [
    {
      index: 1,
      id: 'collection-mybed-btn',
      category: t('$collectionButtons.myBedSlide'),
    },
    {
      index: 2,
      id: 'collection-bedding-btn',
      category: t('$collectionButtons.beddingSlide'),
    },
    {
      index: 3,
      id: 'collection-linens-btn',
      category: t('$collectionButtons.linensSlide'),
    },
    {
      index: 4,
      id: 'collection-bath-btn',
      category: t('$collectionButtons.bathSlide'),
    },
    {
      index: 5,
      id: 'collection-rituals-btn',
      category: t('$collectionButtons.ritualsSlide'),
    },
    {
      index: 6,
      id: 'collection-collaborations-btn',
      category: t('$collectionButtons.collaborationsSlide'),
    },
  ];

  const renderButtonSlide = (slide: any): ReactElement => (
    <button
      key={slide.id}
      id={slide.id}
      type="button"
      className={`btn-plain-light ${isActiveButton === slide.index ? 'active' : ''}`}
      onClick={() => handleClick(slide.index)}
    >
      {slide.category}
    </button>
  );

  const renderStaticSlides = (staticSlides: any[]): ReactElement => (
    <>
      {staticSlides.map(slide => (
        <button
          key={slide.id}
          id={slide.id}
          type="button"
          className={`btn-plain-light ${isActiveButton === slide.index ? 'active' : ''}`}
          onClick={() => handleClick(slide.index)}
        >
          {slide.category}
        </button>
      ))}
    </>
  );

  const myBedSlides = [
    {
      id: 1,
      title: t('$carousel.$myBedSwiper.firstSlideTitle'),
      description: t('$carousel.$myBedSwiper.firstSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-mybed.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-mybed_hover.webp',
      link: tLink('myBed'),
      // eagerLoad: true,
    },
    {
      id: 2,
      title: t('$carousel.$myBedSwiper.secondSlideTitle'),
      description: t('$carousel.$myBedSwiper.secondSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-mybed-luxe.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-mybed-luxe_hover.webp',
      link: tLink('myBedLuxe'),
      // eagerLoad: true,
    },
    {
      id: 3,
      title: t('$carousel.$myBedSwiper.thirdSlideTitle'),
      description: t('$carousel.$myBedSwiper.thirdSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-mybed-bed-bedding-sets.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-mybed-bed-bedding-sets_hover.webp',
      link: tLink('bedBeddingSets'),
    },
    {
      id: 4,
      title: t('$carousel.$myBedSwiper.fourthSlideTitle'),
      description: t('$carousel.$myBedSwiper.fourthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-mybed-bases.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-mybed-bases_hover.webp',
      link: tLink('bedBases'),
    },
    {
      id: 5,
      title: t('$carousel.$myBedSwiper.fifthSlideTitle'),
      description: t('$carousel.$myBedSwiper.fifthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-mybed-headboards.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-mybed-headboards_hover.webp',
      link: tLink('headboards'),
    },
    {
      id: 6,
      title: t('$carousel.$myBedSwiper.sixthSlideTitle'),
      description: t('$carousel.$myBedSwiper.sixthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-mybed-bedding-sets.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-mybed-bedding-sets_hover.webp',
      link: tLink('beddingSets'),
    },
  ];

  const beddingSlides = [
    {
      id: 1,
      title: t('$carousel.$beddingSwiper.firstSlideTitle'),
      description: t('$carousel.$beddingSwiper.firstSlideDescription'),
      imageSrc: '/images/homePage/banners/collection-pillows.webp',
      imageHoverSrc: '/images/homePage/banners/collection-pillows_hover.webp',
      link: tLink('pillows'),
    },
    {
      id: 2,
      title: t('$carousel.$beddingSwiper.secondSlideTitle'),
      description: t('$carousel.$beddingSwiper.secondSlideDescription'),
      imageSrc: '/images/homePage/banners/collection-duvets.webp',
      imageHoverSrc: '/images/homePage/banners/collection-duvets_hover.webp',
      link: tLink('duvets'),
    },
    {
      id: 3,
      title: t('$carousel.$beddingSwiper.thirdSlideTitle'),
      description: t('$carousel.$beddingSwiper.thirdSlideDescription'),
      imageSrc: '/images/homePage/banners/collection-featherbed.webp',
      imageHoverSrc: '/images/homePage/banners/collection-featherbed_hover.webp',
      link: tLink('featherbed'),
    },
    {
      id: 4,
      title: t('$carousel.$beddingSwiper.fourthSlideTitle'),
      description: t('$carousel.$beddingSwiper.fourthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-bedding-sets.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-bedding-sets_hover.webp',
      link: tLink('beddingSets'),
    },
    {
      id: 5,
      title: t('$carousel.$beddingSwiper.fifthSlideTitle'),
      description: t('$carousel.$beddingSwiper.fifthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-pillows-duvet-set.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-pillows-duvet-set_hover.webp',
      link: tLink('pillowsDuvetSets'),
    },
    {
      id: 6,
      title: t('$carousel.$beddingSwiper.sixthSlideTitle'),
      description: t('$carousel.$beddingSwiper.sixthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-bedding-protectors.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-bedding-protectors_hover.webp',
      link: tLink('beddingProtectors'),
    },
  ];

  const linensSlides = [
    {
      id: 1,
      title: t('$carousel.$linensSwiper.firstSlideTitle'),
      description: t('$carousel.$linensSwiper.firstSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-linen-sets.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-linen-sets_hover.webp',
      link: tLink('linenSets'),
    },
    {
      id: 2,
      title: t('$carousel.$linensSwiper.secondSlideTitle'),
      description: t('$carousel.$linensSwiper.secondSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-duvet-cover-pillow-shams-sets.webp',
      imageHoverSrc:
        '/images/homePage/banners/hp-collection-duvet-cover-pillow-shams-sets_hover.webp',
      link: tLink('duvetCoverPillowShamSets'),
    },
    {
      id: 3,
      title: t('$carousel.$linensSwiper.thirdSlideTitle'),
      description: t('$carousel.$linensSwiper.thirdSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-fitted-sheets.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-fitted-sheets_hover.webp',
      link: tLink('fittedSheets'),
    },
    {
      id: 4,
      title: t('$carousel.$linensSwiper.fourthSlideTitle'),
      description: t('$carousel.$linensSwiper.fourthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-pillowcases-pillow-shams.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-pillowcases-pillow-shams_hover.webp',
      link: tLink('pillowcasesPillowShams'),
    },
    {
      id: 5,
      title: t('$carousel.$linensSwiper.fifthSlideTitle'),
      description: t('$carousel.$linensSwiper.fifthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-duvet-covers.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-duvet-covers_hover.webp',
      link: tLink('duvetCovers'),
    },
    {
      id: 6,
      title: t('$carousel.$linensSwiper.sixthSlideTitle'),
      description: t('$carousel.$linensSwiper.sixthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-flat-sheets.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-flat-sheets_hover.webp',
      link: tLink('flatSheets'),
    },
  ];

  const bathSlides = [
    {
      id: 1,
      title: t('$carousel.$bathSwiper.firstSlideTitle'),
      description: t('$carousel.$bathSwiper.firstSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-towel-set.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-towel-set_hover.webp',
      link: tLink('towelSet'),
    },
    {
      id: 2,
      title: t('$carousel.$bathSwiper.secondSlideTitle'),
      description: t('$carousel.$bathSwiper.secondSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-hooded-robe.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-hooded-robe_hover.webp',
      link: tLink('hoodedRobe'),
    },
    {
      id: 3,
      title: t('$carousel.$bathSwiper.thirdSlideTitle'),
      description: t('$carousel.$bathSwiper.thirdSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-body-towel.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-body-towel_hover.webp',
      link: tLink('bodyTowel'),
    },
    {
      id: 4,
      title: t('$carousel.$bathSwiper.fourthSlideTitle'),
      description: t('$carousel.$bathSwiper.fourthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-shawl-collar-robe.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-shawl-collar-robe_hover.webp',
      link: tLink('shawlCollarRobe'),
    },
    {
      id: 5,
      title: t('$carousel.$bathSwiper.fifthSlideTitle'),
      description: t('$carousel.$bathSwiper.fifthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-kids-robe.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-kids-robe_hover.webp',
      link: tLink('kidsRobe'),
    },
    {
      id: 6,
      title: t('$carousel.$bathSwiper.sixthSlideTitle'),
      description: t('$carousel.$bathSwiper.sixthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-hooded-baby-cape.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-hooded-baby-cape_hover.webp',
      link: tLink('hoodedBabyCape'),
    },
  ];

  const ritualsSlides = [
    {
      id: 1,
      title: t('$carousel.$ritualsSwiper.firstSlideTitle'),
      description: t('$carousel.$ritualsSwiper.firstSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-rituals-herbal-tea.webp',
      imageHoverSrc: '/images/homePage/banners/hp-rituals-herbal-tea_hover.webp',
      link: tLink('herbalTea'),
    },
    {
      id: 2,
      title: t('$carousel.$ritualsSwiper.secondSlideTitle'),
      description: t('$carousel.$ritualsSwiper.secondSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-rituals-reed-diffuser.webp',
      imageHoverSrc: '/images/homePage/banners/hp-rituals-reed-diffuser_hover.webp',
      link: tLink('reedDiffuser'),
    },
    {
      id: 3,
      title: t('$carousel.$ritualsSwiper.thirdSlideTitle'),
      description: t('$carousel.$ritualsSwiper.thirdSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-rituals-tea.webp',
      imageHoverSrc: '/images/homePage/banners/hp-rituals-tea_hover.webp',
      link: tLink('tea'),
    },
    {
      id: 4,
      title: t('$carousel.$ritualsSwiper.fourthSlideTitle'),
      description: t('$carousel.$ritualsSwiper.fourthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-rituals-candle2.webp',
      imageHoverSrc: '/images/homePage/banners/hp-rituals-candle2_hover.webp',
      link: tLink('candle'),
    },
    {
      id: 5,
      title: t('$carousel.$ritualsSwiper.fifthSlideTitle'),
      description: t('$carousel.$ritualsSwiper.fifthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-rituals-home-mist.webp',
      imageHoverSrc: '/images/homePage/banners/hp-rituals-home-mist_hover.webp',
      link: tLink('homeMist'),
    },
    {
      id: 6,
      title: t('$carousel.$ritualsSwiper.sixthSlideTitle'),
      description: t('$carousel.$ritualsSwiper.sixthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-rituals-scent-machine.webp',
      imageHoverSrc: '/images/homePage/banners/hp-rituals-scent-machine_hover.webp',
      link: tLink('scentMachine'),
    },
  ];

  const collaborationSlides = [
    {
      id: 1,
      title: t('$carousel.$collaborationsSwiper.firstSlideTitle'),
      description: t('$carousel.$collaborationsSwiper.firstSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-votivelight.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-votivelight_hover.webp',
      link: tLink('votivelight'),
    },
    {
      id: 2,
      title: t('$carousel.$collaborationsSwiper.secondSlideTitle'),
      description: t('$carousel.$collaborationsSwiper.secondSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-classic-swim-shorts.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-classic-swim-shorts_hover.webp',
      link: tLink('classicSwimShorts'),
    },
    {
      id: 3,
      title: t('$carousel.$collaborationsSwiper.thirdSlideTitle'),
      description: t('$carousel.$collaborationsSwiper.thirdSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-sunbed-beach-towel.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-sunbed-beach-towel_hover.webp',
      link: tLink('sunbedBeachTowel'),
    },
    {
      id: 4,
      title: t('$carousel.$collaborationsSwiper.fourthSlideTitle'),
      description: t('$carousel.$collaborationsSwiper.fourthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-beach-cushions.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-beach-cushions_hover.webp',
      link: tLink('camaratBeachCushion'),
    },
    {
      id: 5,
      title: t('$carousel.$collaborationsSwiper.fifthSlideTitle'),
      description: t('$carousel.$collaborationsSwiper.fifthSlideDescription'),
      imageSrc: '/images/homePage/banners/hp-collection-indigo-beach-cushion.webp',
      imageHoverSrc: '/images/homePage/banners/hp-collection-indigo-beach-cushion_hover.webp',
      link: tLink('indigoBeachCushion'),
    },
  ];

  const [firstImgLoaded, setFirstImgLoaded] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const handleMouseEnter = (index: number) => {
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  const renderSlide = (slide: any, index: number): ReactElement => (
    <Link href={slide.link} noTabbing data-event-name="bloc_interact" data-bloc-name={slide.title}>
      <div className="hover-btn-animation">
        <div
          className="image-container"
          onMouseEnter={() => handleMouseEnter(index)}
          onMouseLeave={handleMouseLeave}
        >
          <img
            loading={slide.eagerLoad || firstImgLoaded ? 'eager' : 'lazy'}
            data-src={slide.imageSrc}
            alt={slide.title}
            src={slide.imageSrc}
            width={product.imageWidth}
            height={product.imageHeight}
            onLoad={() => {
              setFirstImgLoaded(true);
            }}
          />
          <img
            data-src={slide.imageHoverSrc}
            alt={slide.title}
            src={slide.imageHoverSrc}
            loading="lazy"
            width={product.imageWidth}
            height={product.imageHeight}
            className={`hover ${hoveredIndex === index ? 'hover-active' : ''}`}
          />
        </div>
        <div className="hover-content desktop-only">
          <span className="btn base-btn btn-main">
            <span>{tButton('shopNow')}</span>
          </span>
        </div>
      </div>
      <h3>{slide.title}</h3>
      <p className="card-description">{slide.description}</p>
    </Link>
  );
  const slideClassName = (): string => 'card';

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedCategory = sessionStorage.getItem(activeCategory);

      if (savedCategory) {
        const categoryId = parseInt(savedCategory, 10);
        setIsActiveButton(categoryId);

        const section = document.getElementById('hp-sofitel-collection');

        if (section) {
          section.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, []);

  return (
    <section id="hp-sofitel-collection" className="sofitel-collection hp-section">
      {!isMobile ? (
        <h2 className="desktop-only">
          <span>{t('titleDesktop1')}</span>
          <span>{t('titleDesktop2')}</span>
        </h2>
      ) : (
        <h2 className="mobile-only">{t('titleMobile')}</h2>
      )}
      {/* Category Swiper Buttons */}
      <div className="category-wrapper collection-buttons-swiper swiper">
        {isMobile ? (
          <Swiper
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            slides={buttonSlides}
            renderSlide={renderButtonSlide}
            slidesPerView={2.2}
            slidesPerGroup={2} // Move 'X' slides at a time
            spaceBetween={8}
            touchEventsTarget="container"
            containerModifierClass="swiper-container-"
            className="swiper-container swiper-wrapper"
            breakpoints={{
              // when window width is >= 370px
              370: {
                slidesPerView: 2.2,
                slidesPerGroup: 2,
              },
              480: {
                slidesPerView: 3.5,
                slidesPerGroup: 3,
              },
            }}
          />
        ) : (
          renderStaticSlides(buttonSlides)
        )}
      </div>
      <div className="carousel-wrapper">
        {/* Collection Mybed Swiper */}
        <div
          id="collection-mybed-swiper"
          className={`swiper hp-swiper ${isActiveButton === 1 ? 'active' : ''}`}
        >
          <Swiper
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            slides={myBedSlides}
            renderSlide={renderSlide}
            slideClassName={slideClassName}
            navigation // Include navigation
            pagination // Include pagination
            containerModifierClass="swiper-container-"
            className="swiper-container swiper-mybed swiper-wrapper"
            // Default values - making mobile as default
            slidesPerView={1.2}
            slidesPerGroup={1} // Move 'X' slides at a time
            spaceBetween={14} // Add space between slides
            touchEventsTarget="container"
            breakpoints={{
              // when window width is >= 480px
              480: {
                slidesPerView: 1.5,
                slidesPerGroup: 1,
                spaceBetween: 14,
              },
              // when window width is >= 768px
              768: {
                slidesPerView: 2.5,
                slidesPerGroup: 2,
                spaceBetween: 20,
              },
              // when window width is >= 1024px
              1024: {
                slidesPerView: 3.5,
                slidesPerGroup: 3.5,
                spaceBetween: 20,
              },
            }}
          />
        </div>
        {/* Collection bedding Swiper */}
        <div
          id="collection-bedding-swiper"
          className={`swiper hp-swiper ${isActiveButton === 2 ? 'active' : ''}`}
        >
          <Swiper
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            slides={beddingSlides}
            renderSlide={renderSlide}
            slideClassName={slideClassName}
            navigation // Include navigation
            pagination // Include pagination
            containerModifierClass="swiper-container-"
            className="swiper-container swiper-bedding swiper-wrapper"
            // Default values - making mobile as default
            slidesPerView={1.2}
            slidesPerGroup={1} // Move 'X' slides at a time
            spaceBetween={14} // Add space between slides
            touchEventsTarget="container"
            breakpoints={{
              // when window width is >= 480px
              480: {
                slidesPerView: 1.5,
                slidesPerGroup: 1,
                spaceBetween: 14,
              },
              // when window width is >= 768px
              768: {
                slidesPerView: 2.5,
                slidesPerGroup: 2,
                spaceBetween: 20,
              },
              // when window width is >= 1024px
              1024: {
                slidesPerView: 3.5,
                slidesPerGroup: 3.5,
                spaceBetween: 20,
              },
            }}
          />
        </div>
        {/* Collection linens Swiper */}
        <div
          id="collection-linens-swiper"
          className={`swiper hp-swiper ${isActiveButton === 3 ? 'active' : ''}`}
        >
          <Swiper
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            slides={linensSlides}
            renderSlide={renderSlide}
            slideClassName={slideClassName}
            navigation // Include navigation
            pagination // Include pagination
            containerModifierClass="swiper-container-"
            className="swiper-container swiper-linens swiper-wrapper"
            // Default values - making mobile as default
            slidesPerView={1.2}
            slidesPerGroup={1} // Move 'X' slides at a time
            spaceBetween={14} // Add space between slides
            touchEventsTarget="container"
            breakpoints={{
              // when window width is >= 480px
              480: {
                slidesPerView: 1.5,
                slidesPerGroup: 1,
                spaceBetween: 14,
              },
              // when window width is >= 768px
              768: {
                slidesPerView: 2.5,
                slidesPerGroup: 2,
                spaceBetween: 20,
              },
              // when window width is >= 1024px
              1024: {
                slidesPerView: 3.5,
                slidesPerGroup: 3.5,
                spaceBetween: 20,
              },
            }}
          />
        </div>
        {/* Collection bath Swiper */}
        <div
          id="collection-bath-swiper"
          className={`swiper hp-swiper ${isActiveButton === 4 ? 'active' : ''}`}
        >
          <Swiper
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            slides={bathSlides}
            renderSlide={renderSlide}
            slideClassName={slideClassName}
            navigation // Include navigation
            pagination // Include pagination
            containerModifierClass="swiper-container-"
            className="swiper-container swiper-bath swiper-wrapper"
            // Default values - making mobile as default
            slidesPerView={1.2}
            slidesPerGroup={1} // Move 'X' slides at a time
            spaceBetween={14} // Add space between slides
            touchEventsTarget="container"
            breakpoints={{
              // when window width is >= 480px
              480: {
                slidesPerView: 1.5,
                slidesPerGroup: 1,
                spaceBetween: 14,
              },
              // when window width is >= 768px
              768: {
                slidesPerView: 2.5,
                slidesPerGroup: 2,
                spaceBetween: 20,
              },
              // when window width is >= 1024px
              1024: {
                slidesPerView: 3.5,
                slidesPerGroup: 3.5,
                spaceBetween: 20,
              },
            }}
          />
        </div>
        {/* Collection rituals Swiper */}
        <div
          id="collection-rituals-swiper"
          className={`swiper hp-swiper ${isActiveButton === 5 ? 'active' : ''}`}
        >
          <Swiper
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            slides={ritualsSlides}
            renderSlide={renderSlide}
            slideClassName={slideClassName}
            navigation // Include navigation
            pagination // Include pagination
            containerModifierClass="swiper-container-"
            className="swiper-container swiper-rituals swiper-wrapper"
            // Default values - making mobile as default
            slidesPerView={1.2}
            slidesPerGroup={1} // Move 'X' slides at a time
            spaceBetween={14} // Add space between slides
            touchEventsTarget="container"
            breakpoints={{
              // when window width is >= 480px
              480: {
                slidesPerView: 1.5,
                slidesPerGroup: 1,
                spaceBetween: 14,
              },
              // when window width is >= 768px
              768: {
                slidesPerView: 2.5,
                slidesPerGroup: 2,
                spaceBetween: 20,
              },
              // when window width is >= 1024px
              1024: {
                slidesPerView: 3.5,
                slidesPerGroup: 3.5,
                spaceBetween: 20,
              },
            }}
          />
        </div>
        {/* Collection collaborations Swiper */}
        <div
          id="collection-collaborations-swiper"
          className={`swiper hp-swiper ${isActiveButton === 6 ? 'active' : ''}`}
        >
          <Swiper
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            slides={collaborationSlides}
            renderSlide={renderSlide}
            slideClassName={slideClassName}
            navigation // Include navigation
            pagination // Include pagination
            containerModifierClass="swiper-container-"
            className="swiper-container swiper-collaborations swiper-wrapper"
            // Default values - making mobile as default
            slidesPerView={1.2}
            slidesPerGroup={1} // Move 'X' slides at a time
            spaceBetween={14} // Add space between slides
            touchEventsTarget="container"
            breakpoints={{
              // when window width is >= 480px
              480: {
                slidesPerView: 1.5,
                slidesPerGroup: 1,
                spaceBetween: 14,
              },
              // when window width is >= 768px
              768: {
                slidesPerView: 2.5,
                slidesPerGroup: 2,
                spaceBetween: 20,
              },
              // when window width is >= 1024px
              1024: {
                slidesPerView: 3.5,
                slidesPerGroup: 3.5,
                spaceBetween: 20,
              },
            }}
          />
        </div>
        {/* Add more swipers here, one for each collection button */}
      </div>
    </section>
  );
};

export default HomeCollectionSection;
